// Design Tokens
export const colors = {
  // Primary Colors
  primary: {
    orange: '#FF6B35',
    light: '#FF8B5C',
    dark: '#E85826',
  },
  
  // Secondary Colors
  secondary: {
    matchaGreen: '#7AC74F',
    taroPurple: '#B4A0D5',
    bobaBrown: '#8B5E3C',
  },
  
  // Accent Colors
  accent: {
    strawberryPink: '#FFB5D6',
    ubePurple: '#9B6B9E',
    creamYellow: '#FFF6E9',
  },
  
  // Neutral Colors
  neutral: {
    100: '#FFFFFF',
    200: '#FFF9F5',
    300: '#F7F3F0',
    400: '#E8E3E0',
    500: '#A8A29E',
    600: '#78716C',
    700: '#44403C',
    800: '#292524',
  }
};

// Typography System
export const typography = {
  fontFamily: {
    heading: 'var(--font-outfit)',
    body: 'var(--font-dm-sans)',
  },
  
  fontSize: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    base: '1rem',     // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    '2xl': '1.5rem',  // 24px
    '3xl': '1.875rem',// 30px
    '4xl': '2.25rem', // 36px
    '5xl': '3rem',    // 48px
  },

  lineHeight: {
    tight: '1.2',
    base: '1.5',
    relaxed: '1.75',
  }
};

// Spacing System
export const spacing = {
  // Base spacing units
  1: '0.25rem',   //  4px
  2: '0.5rem',    //  8px
  3: '0.75rem',   // 12px
  4: '1rem',      // 16px
  6: '1.5rem',    // 24px
  8: '2rem',      // 32px
  12: '3rem',     // 48px
  16: '4rem',     // 64px
  20: '5rem',     // 80px
  24: '6rem',     // 96px
};

// Layout System
export const layout = {
  // Maximum widths
  maxWidth: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1400px',
  },
  
  // Section spacing
  sectionSpacing: {
    sm: '4rem',    // 64px
    md: '6rem',    // 96px
    lg: '8rem',    // 128px
  },
  
  // Container padding
  containerPadding: {
    mobile: '1.5rem',
    tablet: '2.5rem',
    desktop: '4rem',
  }
};

// Border Radius
export const borderRadius = {
  sm: '0.375rem',
  md: '0.5rem',
  lg: '1rem',
  xl: '1.5rem',
  '2xl': '2rem',
  full: '9999px',
};

// Shadows
export const shadows = {
  sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
  md: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
  lg: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
  xl: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
  hover: '0 10px 30px -10px rgba(230,115,0,0.3)',
};

// Animation System
export const animations = {
  // Fade and slide up animation
  fadeInUp: {
    initial: { opacity: 0, y: 10 },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  },
  
  // Hover scale animation
  hoverScale: {
    whileHover: { 
      scale: 1.01,
      transition: { duration: 0.2 }
    }
  },
  
  // Tap scale animation
  tapScale: {
    whileTap: { scale: 0.98 }
  },
  
  // Floating animation
  floatingAnimation: {
    animate: {
      y: [0, -5, 0],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  }
};

// Component-specific styles
export const componentStyles = {
  // Button styles
  button: {
    base: "rounded-full font-medium transition-all duration-300",
    sizes: {
      sm: "px-4 py-1.5 text-sm",
      md: "px-6 py-2 text-base",
      lg: "px-8 py-3 text-lg",
    },
    variants: {
      primary: "bg-primary-orange hover:bg-primary-light text-white shadow-md hover:shadow-lg",
      secondary: "border-2 border-primary-orange/20 hover:border-primary-orange/40 text-primary-orange hover:bg-primary-orange/5",
      ghost: "hover:text-primary-orange hover:bg-primary-orange/5",
    }
  },

  // Card styles
  card: {
    base: "overflow-hidden rounded-2xl border transition-all duration-300 backdrop-blur-sm",
    variants: {
      default: "border-neutral-200/60 hover:border-neutral-300/80 bg-white/95 shadow-sm hover:shadow-lg hover:shadow-primary-orange/5",
      featured: "border-primary-orange/30 hover:border-primary-orange/50 bg-white/98 shadow-lg hover:shadow-xl hover:shadow-primary-orange/10",
    }
  },

  // Section styles
  section: {
    base: "relative overflow-hidden w-full",
    padding: {
      sm: "py-16",
      md: "py-24",
      lg: "py-32",
    },
    variants: {
      light: "bg-white",
      cream: "bg-[#FFF9F5]",
      gradient: "bg-gradient-to-b from-white via-[#FFF9F5] to-transparent",
    }
  },

  // Container styles
  container: {
    base: "mx-auto relative",
    padding: {
      mobile: "px-4",
      tablet: "px-6",
      desktop: "px-8",
    },
    maxWidth: "max-w-[1400px]",
  }
};

// Gradient backgrounds
export const gradients = {
  primary: "bg-gradient-to-br from-primary-orange to-primary-light",
  secondary: "bg-gradient-to-br from-secondary-matchaGreen to-secondary-taroPurple",
  accent: "bg-gradient-to-br from-accent-strawberryPink to-accent-ubePurple",
  background: {
    light: "bg-gradient-to-b from-white via-white to-transparent",
    cream: "bg-gradient-to-b from-neutral-50 to-[#FFF5E6]",
  }
}; 